/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, {
    useEffect, useState, useMemo,
} from 'react';
import { withStyles } from '@material-ui/core/styles';
import {
    bool,
    object,
    string,
} from 'prop-types';
import { Link, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import {
    FormControl, TextField, Button, Avatar,
} from '@material-ui/core';
import { Helmet } from 'react-helmet';
import mbpUtil from '@api-util/mbp';
import { useAuth } from '../../../../state/ducks/Member/ducks/Auth/Plugin/auth';
import ResponsiveImage from '../GraphqlCommonComponents/ResponsiveImage/ResponsiveImage';
import { getBrand } from '../../../pages/Account/state/ducks/Common/Common-Selectors';
import { emitCustomTrackingEvent } from '../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';
import { fetchUserDetailsByEmailApi } from '../../../../apis/account-apis/fetchUserDetailsByEmailApi';
import { fetchActiveSocialConnections } from '../../../../apis/account-apis/fetchActiveSocialConnections';
import SiteLoader from '../../../pages/Checkout/containers/Common/SiteLoader/SiteLoader';
import { getLastVisted } from '../../../../state/ducks/Member/ducks/Common/Common-Selectors';
import { getFeatureFlag } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';

const styles = (theme) => ({
    mainContainer: {
        backgroundColor: '#fafafa',
        [theme.breakpoints.down(500)]: {
            backgroundColor: 'white',
        },
    },
    gridItem: {
        minHeight: 'calc(100vh - 53px)',
        width: '400px',
        '& .MuiPaper-root': {
            boxShadow: 'rgba(0, 0, 0, 0.12) 0px 0px 5px 0px',
            borderRadius: '5px',
            border: '0 solid transparent',
            [theme.breakpoints.down(500)]: {
                boxShadow: 'none',
            },
        },
        '& .MuiCard-root': {
            width: '100%',
        },
        '& .MuiCardContent-root': {
            padding: '30px',
        },
        [theme.breakpoints.down(500)]: {
            width: '100%',
            alignItems: 'flex-start !important',
        },
        '@media (max-height: 600px)': {
            margin: '8px 0px',
        },
    },
    backButton: {
        display: 'flex',
        alignItems: 'center',
        color: 'black',
        textDecoration: 'none',
        '& svg': {
            marginRight: '5px',
            height: '22px',
            width: '22px',
        },
    },
    logoContainer: {
        display: 'flex',
        justifyContent: 'center',
        margin: '30px 0px 20px 0px',
    },
    signInContainer: {
        '& .login': {
            fontFamily: 'area-variable, sans-serif',
            '& h1': {
                textAlign: 'center',
                fontFamily: 'area-variable, sans-serif',
                fontSize: '24px',
                fontStyle: 'normal',
                fontWeight: '700',
                lineHeight: '25px',
                letterSpacing: '0.2px',
            },
            '& h2': {
                textAlign: 'center',
                fontFamily: 'area-variable, sans-serif',
                fontSize: '18px',
                fontStyle: 'normal',
                fontWeight: '400',
                lineHeight: '23px',
                letterSpacing: '0.18px',
            },
            '& p': {
                textAlign: 'center',
                marginTop: '12px',
                '&#user-email-helper-text': {
                    textAlign: 'left',
                    marginTop: '3px',
                },
            },
            '& input, & fieldset, & label': {
                fontSize: '16px',
            },
            '& .Mui-focused fieldset': {
                borderColor: 'black !important',
            },
            '& label:not(.Mui-error).Mui-focused': {
                color: '#000000',
            },
            '& .MuiFormControl-root': {
                marginTop: '15px',
            },
            '& .MuiFilledInput-root': {
                borderRadius: '3px',
                border: '1px solid #000000',
                background: '#FAFAFA',
                '&.Mui-error': {
                    borderColor: 'red',
                },
            },
            '& .MuiFormHelperText-root': {
                marginLeft: 0,
            },
        },
    },
    separator: {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        textTransform: 'uppercase',
        border: 'none',
        fontSize: '16px',
        fontWeight: '700',
        padding: '15px 0 10px 0px',
        '&:after, &:before': {
            content: '""',
            borderBottom: '1px solid #B2B2B2',
            flex: '1 0 auto',
            height: '14px',
        },
        '& span': {
            margin: '0px 10px 0px 10px',
        },
    },
    checkbox: {
        '& .MuiFormControlLabel-label': {
            fontSize: '14px',
            fontFamily: 'Lato',
            color: '#21242D',
        },
    },
    continueButton: {
        display: 'block',
        width: '100%',
        backgroundColor: 'black',
        fontSize: '18px',
        fontWeight: '700',
        textTransform: 'none',
        marginTop: '24px',
        letterSpacing: '0.04em',
        height: '52px',
        '&:hover': {
            backgroundColor: 'black',
        },
        '&.otp': {
            backgroundColor: 'white',
            color: 'black',
            border: '1px solid black',
            fontWeight: '500',
        },
    },
    privacyPolicyBlock: {
        fontSize: '14px',
        textAlign: 'center',
        marginTop: '10px',
        '& a': {
            color: '#0066CC',
            margin: '0px 5px 0px 5px',
            textDecoration: 'underline',
        },
    },
    footer: {
        width: '100%',
        background: '#D9D9D9',
        height: '53px',
        '& a': {
            color: 'black',
            textDecoration: 'none',
            padding: '0px 15px 0px 15px',
        },
    },
    bottomContainer: {
        '& .secureTag': {
            textAlign: 'center',
            marginTop: '5px',
            fontWeight: '400',
            fontSize: '14px',
        },
    },
    displayFlexCenter: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    loader: {
        top: 'auto',
        left: 'auto',
    },
    socialLogin: {
        fontSize: '14px',
        color: '#2F2F2F',
        marginTop: '24px',
        '& button': {
            marginLeft: '20px',
            backgroundColor: 'white',
            height: '56px',
            border: '1px solid black',
            padding: '0px',
            boxShadow: 'none',
            '& .MuiButton-startIcon': {
                margin: '0px',
            },
            '& .MuiAvatar-root': {
                height: '26px',
                width: '26px',
            },
            '&:hover': {
                backgroundColor: 'white',
            },
        },
    },
});

/* eslint-disable quote-props */
const brandLinks = {
    '18F': {
        logo: 'https://images.contentstack.io/v3/assets/blt95e0138311135214/blt70152fe69e54bf98/5f08974aaf2a927f93b3b3ec/18f-logo-v2.svg',
        helpLink: '/customer-service',
    },
    'HD': {
        logo: 'https://images.contentstack.io/v3/assets/blt95e0138311135214/blt13b0f02cd71877df/5f08974a8b0e59084c9f7570/hd-logo-v3.svg',
        helpLink: '/h/view/custserv',
    },
    'PML': {
        logo: 'https://images.contentstack.io/v3/assets/blt95e0138311135214/bltac334446d6f9e89c/5f96fc4f2425cd7a8af6845e/pmall_svg-logo-v2.svg',
        helpLink: '/ContactUs.aspx',
    },
    'BRY': {
        logo: 'https://images.contentstack.io/v3/assets/blt95e0138311135214/bltf0b1021465476e4f/5f08974b0d1d2008538ce15a/SharisLogo_r.svg',
        helpLink: '/customer-service',
    },
    'SCH': {
        logo: 'https://images.contentstack.io/v3/assets/blt95e0138311135214/blt71adcb0696952e83/5f08974baa5d1801639286b4/sch-logo-v2.svg',
        helpLink: '/customer-service',
    },
    'FB': {
        logo: 'https://images.contentstack.io/v3/assets/blt95e0138311135214/bltef2e14ec247ab827/5f08974a2cd8e87f92bdd257/fb-logo-v2.svg',
        helpLink: '/customer-service',
    },
    'CCO': {
        logo: 'https://images.contentstack.io/v3/assets/blt95e0138311135214/blte1e2eb25cfd09fb6/5f08974a91afb901b86d5251/cco-logo-v2.svg',
        helpLink: '/cheryls-customer-service',
    },
    'TPF': {
        logo: 'https://images.contentstack.io/v3/assets/blt95e0138311135214/bltf9903ccf2408c217/5f0897552cd8e87f92bdd25b/tpf-logo-v2.svg',
        helpLink: '/customer-service',
    },
    'WLF': {
        logo: 'https://images.contentstack.io/v3/assets/blt95e0138311135214/blt7ef824d3f9f5074d/5f0897558b0e59084c9f7574/wlf-logo-v2.svg',
        helpLink: '/w/view/customerserv',
    },
    'VC': {
        logo: 'https://images.contentstack.io/v3/assets/blt0281495b20b504a0/blta4f709f645453ff0/61b4eeeea000c90ecc6e15bd/VC_Logo_w_tag.svg',
        helpLink: '/content/contact-customer-service',
    },
    '18B': {
        logo: 'https://images.contentstack.io/v3/assets/bltc699f5c4977942f7/blt329c48c98c46443f/64111cfcaa6c3f7f594be64c/1800Baskets_Logo_compact.svg',
        helpLink: '/customer-service-1800baskets',
    },
    'PLA': {
        logo: 'https://images.contentstack.io/v3/assets/blt4454dc0093319d35/blt55247e0014fdfe9f/5f3ec458eb93e224977c1ab8/plants.png',
        helpLink: '/customer-service',
    },
    'FL': {
        logo: 'https://images.contentstack.io/v3/assets/bltdbb9b0b21e621277/blt43421064ecb770d8/6552559c1bfcdb6ca0dd8760/Florists.com_Logo_Color_OT.svg',
        helpLink: '/f/customer-service',
    },
    'CLB': {
        logo: 'https://cdn1.harryanddavid.com/wcsstore/HarryAndDavid/images/Auth0/celebrations-passport-logo.png',
        helpLink: '/customer-service',
    },
};

const NativeSigninUIComponent = ({
    classes,
    isPrompt,
    emailId,
    salesForceData,
}) => {
    const history = useHistory();
    const logoDimensions = {
        height: 60,
        width: 'auto',
    };
    const { loginByRedirect } = useAuth();
    const brand = useSelector(getBrand);
    const authPageGoBackSkipProtectedRoutes = useSelector(getFeatureFlag('auth-page-go-back-skip-protected-routes'));
    const ffGcsStaticPath = useSelector(getFeatureFlag('gcs-static-asset-path'));
    const staticPrefix = ffGcsStaticPath || '';
    let lastVisitedPage = useSelector(getLastVisted);
    if (!lastVisitedPage) lastVisitedPage = '/';
    const dispatch = useDispatch();

    const trackCustomClickEvent = (method) => {
        dispatch(emitCustomTrackingEvent({
            action: 'Click',
            page: { type: `native-signin-${isPrompt ? 'prompt' : 'page'}`, title: `Native Signin ${isPrompt ? 'Prompt' : 'Page'}` },
            label: `native-login-${isPrompt && 'prompt-'}${method}`,
            eventSubType: 'feature-tracking',
            salesforceResponse: salesForceData,
        }));
    };

    const [userEmailData, setUserEmailData] = useState({
        value: emailId,
        error: false,
        errorText: '',
        errorTextDisplay: '',
    });
    const [showLoader, setShowLoader] = useState(false);
    const [mounted, setMounted] = useState(false);
    const [activeSocialConnections, setActiveSocialConnections] = useState([]);
    const brandName = brand['display-name'];
    const reCAPTCHA_site_key = mbpUtil.getEnv('APP_G_RECAPTCHA_INTERSTITIAL_V3_KEY');

    useEffect(() => {
        const fetchSocialConnections = async () => {
            const response = await fetchActiveSocialConnections(brand?.auth_client_id);
            const activeconnections = response.activeconnections;
            const sortedConnections = activeconnections.sort((a, b) => {
                const order = { 'google': 1, 'apple': 2, 'facebook': 3 };
                return order[a.name] - order[b.name];
            });
            setActiveSocialConnections(sortedConnections);
            setMounted(true);
        };
        fetchSocialConnections();
    }, []);

    useEffect(() => {
        if (userEmailData?.value !== emailId) {
            setUserEmailData({
                ...userEmailData,
                value: emailId,
            });
        }
    }, [emailId]);

    const handleChange = (e) => {
        const { id, value } = e.target;
        const requiredEmailError = { error: true, errorText: 'Email is required.', value };
        const invalidEmailError = { error: true, errorText: 'Email is not valid.', value };
        const validEmailData = { error: false, errorText: '', value };
        // eslint-disable-next-line no-useless-escape
        const reg = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (id === 'user-email') {
            if (!value) setUserEmailData({ ...userEmailData, ...requiredEmailError });
            else if (value && !reg.test(value)) setUserEmailData({ ...userEmailData, ...invalidEmailError });
            else setUserEmailData({ ...userEmailData, ...validEmailData });
        }
    };

    const handleGoBack = () => {
        const normalizedLastPage = (lastVisitedPage || '').trim().toLowerCase().replace(/^\/+/, '/');
        if (!normalizedLastPage || authPageGoBackSkipProtectedRoutes?.includes(normalizedLastPage)) {
            return history.replace('/');
        }

        return history.replace(normalizedLastPage);
    };

    const encryptEmail = (publicKeyBase64, email) => {
        try {
            // eslint-disable-next-line no-undef
            const publicKey = KEYUTIL.getKey(publicKeyBase64);
            // eslint-disable-next-line no-undef
            const encryptor = new JSEncrypt();
            encryptor.setKey(publicKey);
            const encrypted = encryptor.encrypt(email);
            return encrypted;
        } catch (error) {
            console.error('Encryption error:', error);
            return null;
        }
    };

    const submitAction = async (login_type) => {
        trackCustomClickEvent('continue');
        const requiredEmailError = { error: true, errorText: 'Email is required.', errorTextDisplay: 'Email is required.' };
        if (!userEmailData.value) setUserEmailData({ ...userEmailData, ...requiredEmailError });
        if (userEmailData.value && !userEmailData.error) {
            setShowLoader(true);
            setUserEmailData({ ...userEmailData, errorTextDisplay: userEmailData.errorText });
            const email = userEmailData.value;
            const publicKeyBase64 = mbpUtil.getEnv('APP_PUBLIC_KEY_FOR_EMAIL_ENCRYPTION');
            const publicKeyPEM = `-----BEGIN PUBLIC KEY-----\n${publicKeyBase64}\n-----END PUBLIC KEY-----\n`;
            const encryptedEmail = encryptEmail(publicKeyPEM, email);
            // eslint-disable-next-line no-undef
            grecaptcha.ready(() => {
                // eslint-disable-next-line no-undef
                grecaptcha.execute(reCAPTCHA_site_key, { action: 'submit' }).then(async (token) => {
                    const body = {
                        token,
                        email: encryptedEmail,
                    };
                    const userReponse = await fetchUserDetailsByEmailApi(body);
                    setShowLoader(false);
                    const options = {
                        login_hint: email,
                    };
                    if (lastVisitedPage.includes('/order-confirmation/')) options['ext-from-page'] = 'order-confirmation';
                    if (userReponse.code === 200 || userReponse.code === 412) {
                        if (userReponse?.quickSignUp === 'true' && userReponse?.passwordChanged === 'false') options['connection'] = 'email';
                        loginByRedirect(options, { register: userReponse?.status ? 'N' : 'Y' });
                    } else loginByRedirect(options);
                });
            });
        } else if (userEmailData.error) {
            setUserEmailData({ ...userEmailData, errorTextDisplay: userEmailData.errorText });
        }
    };

    /* eslint-disable react/prop-types */
    const SocialButton = ({ connection }) => (
        <Button
            variant="contained"
            color="secondary"
            startIcon={<Avatar alt={connection.name} src={`${staticPrefix}/static/images/${connection.name}.svg`} />}
            onClick={async (e) => {
                trackCustomClickEvent(connection.name);
                await loginByRedirect({ connection: connection.strategy });
            }}
            aria-label={`${connection.name} Login`}
        />
    );

    const socialLoginSection = useMemo(() => {
        if (activeSocialConnections.length > 0) {
            return (
                <div className={`${classes.socialLogin} ${classes.displayFlexCenter}`}>
                    or sign in with
                    {activeSocialConnections.map((connection) => (
                        <SocialButton key={connection.name} connection={connection} />
                    ))}
                </div>
            );
        }
        return null;
    }, [activeSocialConnections, classes.socialLogin, classes.displayFlexCenter]);

    if (!isPrompt && !mounted) return null;

    return (
        <>
            {showLoader && <SiteLoader extraClass={classes.loader} /> }
            <Grid
                container
                spacing={0}
                direction="column"
                alignItems="center"
                className={`${classes.mainContainer} mainContainer`}
            >
                {brandName && !isPrompt && <Helmet><title>{brandName} | Signin</title></Helmet>}
                <Helmet><script src="https://cdnjs.cloudflare.com/ajax/libs/jsrsasign/8.0.20/jsrsasign-all-min.js" /></Helmet>
                <Helmet><script src="https://cdnjs.cloudflare.com/ajax/libs/jsencrypt/3.3.2/jsencrypt.min.js" /></Helmet>
                <Helmet><script src={`https://www.google.com/recaptcha/api.js?render=${reCAPTCHA_site_key}`} /></Helmet>
                <Grid item className={`${classes.gridItem} ${classes.displayFlexCenter}`} style={{ minHeight: isPrompt ? 'auto' : 'calc(100vh - 53px)', overflow: isPrompt ? 'hidden' : '' }}>
                    <Card>
                        <CardContent>
                            <form onSubmit={(e) => { e.preventDefault(); submitAction('password'); }}>
                                {!isPrompt && (
                                    <Link
                                        to="/"
                                        className={classes.backButton}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleGoBack();
                                        }}
                                    >
                                        <ArrowBackIcon />
                                        <span>Back</span>
                                    </Link>
                                )}
                                {!isPrompt && (
                                    <div className={classes.logoContainer}>
                                        <Link to="/">
                                            <ResponsiveImage
                                                path={brandLinks[brand?.code]?.logo}
                                                alt={brand?.domain}
                                                params={{
                                                    desktop: 'quality=100',
                                                    tablet: 'quality=100',
                                                    mobile: 'quality=100',
                                                }}
                                                dimensions={logoDimensions}
                                            />
                                        </Link>
                                    </div>
                                )}
                                <div className={classes.signInContainer}>
                                    <div className="login">
                                        {isPrompt ? (
                                            <>
                                                <h1>Welcome back!</h1>
                                                <h2>Sign in for a personalized experience <br /> and faster checkout.</h2>
                                            </>
                                        ) : (
                                            <h1>Sign in or Create Account</h1>
                                        )}
                                        {/* <p>Enter your email address to receive order confirmation and shipping updates.</p> */}
                                        <FormControl fullWidth>
                                            <TextField
                                                required
                                                error={userEmailData.errorTextDisplay}
                                                helperText={userEmailData.errorTextDisplay}
                                                value={userEmailData.value}
                                                type="email"
                                                id="user-email"
                                                label="Email address"
                                                variant="filled"
                                                size="medium"
                                                onChange={handleChange}
                                                InputProps={{ disableUnderline: true }}
                                            />
                                        </FormControl>
                                    </div>
                                </div>
                                <div className={classes.bottomContainer} style={{ marginBottom: isPrompt ? '15px' : '' }}>
                                    {/* <FormControlLabel
                                        className={classes.checkbox}
                                        control={<Checkbox name="subscribe" id="subscribe" checked={subscribed} onChange={() => setSubscribed((state) => !state)} />}
                                        label="Email me promotions & special offers"
                                    /> */}
                                    <div className={`${classes.privacyPolicyBlock} privacyPolicyBlock`}>
                                        By continuing, you agree to our
                                        {!isPrompt && <br />}
                                        <a href="/About-Us-Terms-of-Use" target="_blank" rel="noreferrer">TERMS OF USE</a>
                                        and
                                        <a href="/About-Us-Privacy-Policy" target="_blank" rel="noreferrer">PRIVACY NOTICE</a>
                                    </div>
                                    <Button className={classes.continueButton} variant="contained" color="primary" disableElevation size="large" onClick={() => submitAction('password')}>
                                        Continue
                                    </Button>
                                    {/* <div className={classes.separator}>
                                        <span>Or</span>
                                    </div>
                                    <Button className={`${classes.continueButton} otp`} variant="contained" color="primary" disableElevation size="large" onClick={() => submitAction('otp')}>
                                        Get A One-Time Code
                                    </Button>
                                    <p className="secureTag">Sent securely to your email address</p> */}
                                    {socialLoginSection}
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                </Grid>
                {!isPrompt && (
                    <div className={`${classes.footer} ${classes.displayFlexCenter}`}>
                        <a href={brandLinks[brand?.code].helpLink}>Help</a>
                        <a href="/About-Us-Privacy-Policy">Privacy</a>
                        <a href="/About-Us-Terms-of-Use">Terms</a>
                    </div>
                )}
            </Grid>
        </>
    );
};

NativeSigninUIComponent.propTypes = {
    classes: object.isRequired,
    isPrompt: bool,
    emailId: string,
    salesForceData: object.isRequired,
};

NativeSigninUIComponent.defaultProps = {
    isPrompt: false,
    emailId: '',
};

export default withStyles(styles)(NativeSigninUIComponent);
